/**
 * Audio processing utilities for bagpipe sound generation
 */

/**
 * Creates and plays a tone with optional chanter simulation and drone effects
 * @param {number} frequency - The frequency to play in Hz
 * @param {AudioContext} audioContext - The Web Audio context to use
 * @param {Object} options - Configuration options
 * @param {boolean} [options.simulateChanter=false] - Whether to simulate chanter characteristics
 * @param {boolean} [options.includeDrones=false] - Whether to include drone sounds
 * @param {number} [options.droneVolume=0.5] - Volume level for drones (0-1)
 * @param {boolean} [options.continuousPlay=false] - Whether to play continuously
 * @returns {Function} Cleanup function to stop and disconnect audio nodes
 */
export function playTone(frequency, audioContext, options = {}) {
  if (!audioContext || !frequency) return () => {};

  const {
    simulateChanter = false,
    includeDrones = false,
    droneVolume = 0.5,
    continuousPlay = false,
  } = options;

  // Store all audio nodes that need cleanup
  const audioNodes = {
    oscillators: [],
    modulators: [],
    gains: [],
    panners: [],
    other: []
  };

  // Basic audio setup
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
  gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.start();
  audioNodes.oscillators.push(oscillator);
  audioNodes.gains.push(gainNode);

  // Only schedule stop if not in continuous play mode
  if (!continuousPlay) {
    oscillator.stop(audioContext.currentTime + 1);
  }

  // Return cleanup function
  return () => {
    // Stop all oscillators
    audioNodes.oscillators.forEach(osc => {
      try {
        osc.stop();
        osc.disconnect();
      } catch (e) {
        console.error('Error cleaning up oscillator:', e);
      }
    });

    // Disconnect all gains
    audioNodes.gains.forEach(gain => {
      try {
        gain.disconnect();
      } catch (e) {
        console.error('Error cleaning up gain:', e);
      }
    });
  };
}
